import { observable, computed } from 'mobx';
import { get } from '@utils/get';
import ListStore from '@stores/list';

export class UserStore {
    @observable namespace = 'user';

    @observable userInfo = null;
    @observable userMedal = null;
    @observable token = null;
    @observable permissions = [];
    @observable isLoggedIn = false;
    @observable rememberMe = false;
    @observable isLoading = false;
    @observable error = null;
    @observable usersThreadsList = null;  // 帖子列表
    @observable currentUsersCode = '';
    @observable usersPostsList = null;  // 用户评论列表
    @observable usersLikesList = null;  // 用户点赞列表
    @observable usersFavoritesList = null;  // 用户收藏列表
    @observable userMeta = null;    // 用户未读消息数据
    @observable userNotificationsList = null;  // 用户消息列表
    @observable chatList = null;  // 用户消息列表
    @observable chatTotalCount = 0;
    @observable userChatMessageList = null; // 私信消息列表（和用户的聊天记录）
    

    constructor(initData = {}) {
        this.lists = new ListStore();

        // 初始化数据
        if (initData.userInfo) {
            this.userInfo = initData.userInfo;
        }
        if (initData.permissions) {
            this.permissions = initData.permissions;
        }
        if(initData.userMedal) {
            this.userMedal = initData.userMedal;
        }
        if(initData.userMeta) {
            this.userMeta = initData.userMeta;
        }

        this.listStore = new ListStore();
        // 注册业务list
        this.listStore.registerList({ namespace: 'usersThreadsList' });
        this.listStore.registerList({ namespace: 'usersPostsList' });
        this.listStore.registerList({ namespace: 'usersLikesList' });
        this.listStore.registerList({ namespace: 'usersFavoritesList' });
        this.listStore.registerList({ namespace: 'followList' });
        this.listStore.registerList({ namespace: 'recommendUserList' });
        this.listStore.registerList({ namespace: 'userNotificationsList' });
        this.listStore.registerList({ namespace: 'chatList' });
        this.listStore.registerList({ namespace: 'userChatMessageList' });
        
        
        // 初始化数据（兼容 SSR/初始注入）
        if (initData.usersThreadsList) {
            this.listStore.lists['usersThreadsList'].data = initData.usersThreadsList;
        }
    }

    /**
     * 获取用户帖子列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get threadListData() {
        return this.listStore.getList({ namespace: 'usersThreadsList' });
    }

    /**
     * 获取用户帖子列表附加属性（如分页、总数等）
     */
    get threadListAttribs() {
        return this.listStore.lists['usersThreadsList']?.attribs || {};
    }

    /**
     * 获取用户帖子 loading 状态
     */
    get threadListLoading() {
        return this.listStore.lists['usersThreadsList']?.loading || false;
    }

    /**
     * 获取用户帖子错误信息
     */
    get threadListError() {
        return this.listStore.lists['usersThreadsList']?.requestError || { isError: false, errorText: '' };
    }


    /**
     * 获取用户评论列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get threadListData() {
        return this.listStore.getList({ namespace: 'usersPostsList' });
    }

    /**
     * 获取用户评论列表附加属性（如分页、总数等）
     */
    get threadListAttribs() {
        return this.listStore.lists['usersPostsList']?.attribs || {};
    }

    /**
     * 获取用户评论 loading 状态
     */
    get threadListLoading() {
        return this.listStore.lists['usersPostsList']?.loading || false;
    }

    /**
     * 获取用户评论错误信息
     */
    get threadListError() {
        return this.listStore.lists['usersPostsList']?.requestError || { isError: false, errorText: '' };
    }

    /**
     * 获取用户点赞列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get likeListData() {
        return this.listStore.getList({ namespace: 'usersLikesList' });
    }

    /**
     * 获取用户点赞列表附加属性（如分页、总数等）
     */
    get likeListAttribs() {
        return this.listStore.lists['usersLikesList']?.attribs || {};
    }

    /**
     * 获取用户点赞 loading 状态
     */
    get likeListLoading() {
        return this.listStore.lists['usersLikesList']?.loading || false;
    }

    /**
     * 获取用户点赞错误信息
     */
    get likeListError() {
        return this.listStore.lists['usersLikesList']?.requestError || { isError: false, errorText: '' };
    }

    /**
     * 获取用户收藏列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get favoriteListData() {
        return this.listStore.getList({ namespace: 'usersFavoritesList' });
    }

    /**
     * 获取用户收藏列表附加属性（如分页、总数等）
     */
    get favoriteListAttribs() {
        return this.listStore.lists['usersFavoritesList']?.attribs || {};
    }

    /**
     * 获取用户收藏 loading 状态
     */
    get favoriteListLoading() {
        return this.listStore.lists['usersFavoritesList']?.loading || false;
    }

    /**
     * 获取用户收藏错误信息
     */
    get favoriteListError() {
        return this.listStore.lists['usersFavoritesList']?.requestError || { isError: false, errorText: '' };
    }

    /**
     * 获取用户消息列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get userNotificationsListData() {
        return this.listStore.getList({ namespace: 'userNotificationsList' });
    }

    /**
     * 获取用户消息列表附加属性（如分页、总数等）
     */
    get userNotificationsListAttribs() {
        return this.listStore.lists['userNotificationsList']?.attribs || {};
    }

    /**
     * 获取用户消息 loading 状态
     */
    get userNotificationsListLoading() {
        return this.listStore.lists['userNotificationsList']?.loading || false;
    }

    /**
     * 获取用户消息错误信息
     */
    get userNotificationsListError() {
        return this.listStore.lists['userNotificationsList']?.requestError || { isError: false, errorText: '' };
    }

     /**
     * 获取用户私信列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get userChatListData() {
        return this.listStore.getList({ namespace: 'chatList' });
    }

    /**
     * 获取用户私信列表附加属性（如分页、总数等）
     */
    get userChatListAttribs() {
        return this.listStore.lists['chatList']?.attribs || {};
    }

    /**
     * 获取用户私信 loading 状态
     */
    get userChatListLoading() {
        return this.listStore.lists['chatList']?.loading || false;
    }

    /**
     * 获取用户私信错误信息
     */
    get userChatListError() {
        return this.listStore.lists['chatList']?.requestError || { isError: false, errorText: '' };
    }

    /**
     * 获取用户私信列表数据（拍平所有页，适合 list 组件直接渲染）
     */
    get userChatMessageListData() {
        return this.listStore.getList({ namespace: 'userChatMessageList' });
    }

    /**
     * 获取用户私信列表附加属性（如分页、总数等）
     */
    get userChatMessageListAttribs() {
        return this.listStore.lists['userChatMessageList']?.attribs || {};
    }

    /**
     * 获取用户私信 loading 状态
     */
    get userChatMessageListLoading() {
        return this.listStore.lists['userChatMessageList']?.loading || false;
    }

    /**
     * 获取用户私信错误信息
     */
    get userChatMessageListError() {
        return this.listStore.lists['userChatMessageList']?.requestError || { isError: false, errorText: '' };
    }

    @computed get code() {
        return get(this.userInfo, 'code');
    }

    @computed get nickname() {
        return get(this.userInfo, 'nickname');
    }

    @computed get avatar() {
        return get(this.userInfo, 'avatarUrl');
    }

    @computed get isBindWechat() {
        return get(this.userInfo, 'isBindWechat');
    }

    @computed get mobile() {
        return get(this.userInfo, 'mobile');
    }
    @computed get titleMark() {
        return get(this.userInfo, 'titleMark');
    }
}

// 不再导出 userStore 实例，全部用 userAction 实例